// The ?help=<topicId> contract — ONE place owns reading, opening, and closing
// the contextual help panel's URL state.
//
// The param is UI state, not navigation: every write uses replace so leaning
// on help never pollutes history, while the URL stays deep-linkable (copy it
// and the panel reopens on the same topic after refresh). Sheet routes key
// their surfaces off path params only, so toggling the search param re-renders
// without remounting the workspace underneath.
//
// Below ~1100px there is no room for a 440px side panel next to real work, so
// every affordance routes to the full help page instead — same content, no
// cramped in-between state.
import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { helpArticleRoute } from "./content/manifest";
import { parseHelpTopic } from "./helpSurface";

export const HELP_PARAM = "help";
const NARROW_QUERY = "(max-width: 1100px)";

export function isNarrowViewport(): boolean {
  return typeof window !== "undefined" && window.matchMedia(NARROW_QUERY).matches;
}

/** The raw ?help= value ("topic" or "topic#anchor"), or null. */
export function useHelpParam(): string | null {
  const [searchParams] = useSearchParams();
  return searchParams.get(HELP_PARAM);
}

/** Open a help topic contextually: the side panel on desktop, the full page on
 *  narrow viewports. Unknown/unpublished topics no-op (callers should not
 *  render an affordance for one — validate with helpArticleRoute first). */
export function useOpenHelpTopic() {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  return useCallback(
    (topic: string) => {
      const { topicId, anchor } = parseHelpTopic(topic);
      const route = helpArticleRoute(topicId);
      if (!route) return;
      if (isNarrowViewport()) {
        navigate(anchor ? `${route}#${anchor}` : route);
        return;
      }
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set(HELP_PARAM, topic);
          return next;
        },
        { replace: true },
      );
    },
    [navigate, setSearchParams],
  );
}

export function useCloseHelp() {
  const [, setSearchParams] = useSearchParams();
  return useCallback(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(HELP_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
}
